import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Newspaper, Calendar } from "lucide-react";

export default function Media() {
    const [posts, setPosts] = useState([]);
    const [activeCat, setActiveCat] = useState("All");

    useEffect(() => { api.get("/blog").then((r) => setPosts(r.data || [])); }, []);

    const categories = useMemo(
        () => ["All", ...Array.from(new Set(posts.map((p) => p.category).filter(Boolean)))],
        [posts]
    );
    const filtered = activeCat === "All" ? posts : posts.filter((p) => p.category === activeCat);

    return (
        <div data-testid="media-page">
            {/* Hero */}
            <section className="pt-20 pb-10 md:pt-32 md:pb-14">
                <div className="container-hero grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-8">
                        <div className="label-eyebrow text-brand-forest mb-4 inline-flex items-center gap-2">
                            <Newspaper size={14} strokeWidth={1.75} /> Media &amp; News
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl font-semibold leading-[1.05]">
                            Stories from the field, one post at a time.
                        </h1>
                        <p className="text-slate-600 text-lg mt-5 max-w-xl">
                            Press coverage, project updates and stories from the villages we work in — curated as we go.
                        </p>
                    </div>
                    <div className="md:col-span-4">
                        <div className="bg-brand-forest text-white rounded-2xl p-6">
                            <div className="font-display text-5xl font-semibold">{posts.length}</div>
                            <div className="text-xs uppercase tracking-[0.15em] text-white/80 mt-1">
                                Published stories
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filter pills */}
            {categories.length > 2 && (
                <section className="pb-8">
                    <div className="container-hero flex flex-wrap gap-2">
                        {categories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setActiveCat(c)}
                                data-testid={`media-filter-${c}`}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 border ${
                                    activeCat === c
                                        ? "bg-brand-forest text-white border-brand-forest"
                                        : "bg-white border-black/10 text-slate-700 hover:border-brand-forest hover:text-brand-forest"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* Uniform card grid — same height throughout */}
            <section className="pb-24 md:pb-32">
                <div className="container-hero">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filtered.map((b) => (
                            <Link
                                key={b.id}
                                to={`/media/${b.slug}`}
                                data-testid={`post-${b.slug}`}
                                className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-black/5 hover:shadow-lg hover:-translate-y-1 transition-transform duration-300 h-full"
                            >
                                {/* Uniform image container — all cards get the same fixed height */}
                                <div className="relative h-56 overflow-hidden bg-brand-stone">
                                    <img
                                        src={b.cover_image}
                                        alt={b.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                                    />
                                    <div className="absolute top-3 left-3">
                                        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold px-2.5 py-1 rounded-full bg-brand-ink text-white inline-flex items-center gap-1.5">
                                            <Newspaper size={10} strokeWidth={2} /> {b.category}
                                        </span>
                                    </div>
                                </div>

                                {/* Body — flex-1 so all cards stretch to equal height */}
                                <div className="flex flex-col flex-1 p-6">
                                    <h3 className="font-display text-xl leading-snug text-brand-ink line-clamp-3">
                                        {b.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm mt-3 line-clamp-3 flex-1">
                                        {b.excerpt}
                                    </p>
                                    <div className="mt-4 pt-4 border-t border-black/5 flex items-center justify-between text-xs">
                                        <span className="inline-flex items-center gap-1.5 text-slate-500">
                                            <Calendar size={12} strokeWidth={1.75} />
                                            {b.created_at
                                                ? new Date(b.created_at).toLocaleDateString("en-IN", {
                                                      day: "numeric",
                                                      month: "short",
                                                      year: "numeric",
                                                  })
                                                : ""}
                                        </span>
                                        <span className="inline-flex items-center gap-1 font-semibold text-brand-forest group-hover:gap-2 transition-[gap] duration-200">
                                            Read story <ArrowRight size={12} />
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {filtered.length === 0 && (
                        <div className="text-center text-slate-500 py-24">
                            No stories in this category yet.
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
